import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import ComplianceMetrics from '../components/compliance/ComplianceMetrics';
import ComplianceTable from '../components/compliance/ComplianceTable';
import DepartmentComplianceChart from '../components/compliance/DepartmentComplianceChart';
import ComplianceStatusBreakdown from '../components/compliance/ComplianceStatusBreakdown';
import UpcomingDeadlines from '../components/compliance/UpcomingDeadlines';
import ComplianceTrendChart from '../components/compliance/ComplianceTrendChart';
import RequirementTypeSummary from '../components/compliance/RequirementTypeSummary';
import PageHeader from '../components/ui/PageHeader';
import { dataFetch } from '../api';

const { FiList, FiBarChart2, FiLoader } = FiIcons;

const TABS = [
  { id: 'overview', label: 'Overview', icon: FiBarChart2 },
  { id: 'records',  label: 'All Records', icon: FiList },
];

const Compliance = () => {
  const [tab, setTab] = useState('overview');
  const [records, setRecords] = useState([]);
  const [learners, setLearners] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dataFetch(`read/assignments?limit=5000&year=${year}`), // Fetching assignments for the selected year
      dataFetch('read/employees?limit=5000'),
      dataFetch('read/campaigns?limit=5000'),
      dataFetch('read/phishing_simulations?limit=5000'),
      dataFetch('read/phishing_recipients?limit=5000')
    ]).then(([assnRes, empRes, campRes, simRes, recRes]) => {
      const assignments = assnRes?.data ?? [];
      const employees = empRes?.data ?? [];
      const campaignsData = campRes?.data ?? [];
      const simulations = simRes?.data ?? [];
      const recipients = recRes?.data ?? [];

      const constructedRecords = [];
      const today = new Date();

      // Group assignments by campaign and employee to evaluate complete campaign compliance
      const groupedAssignments = {};
      assignments.forEach(a => {
        const key = `${a.campaign_id}_${a.employee_id}`;
        if (!groupedAssignments[key]) groupedAssignments[key] = [];
        groupedAssignments[key].push(a);
      });

      Object.entries(groupedAssignments).forEach(([key, assns]) => {
        const a = assns[0];
        const campaign = campaignsData.find(c => String(c.id) === String(a.campaign_id));
        if (!campaign) return;

        const due = new Date(campaign.endDate);
        const isCompleted = assns.every(asn => asn.status === 'Completed');
        const anyInProgress = assns.some(asn => asn.status === 'In Progress' || asn.status === 'Completed');

        let complianceStatus = 'Not Started';
        if (isCompleted) {
          complianceStatus = 'Compliant';
        } else if (due < today) {
          complianceStatus = 'Overdue';
        } else if (due.getTime() - today.getTime() <= 14 * 24 * 60 * 60 * 1000) {
          complianceStatus = 'At Risk';
        } else if (anyInProgress) {
          complianceStatus = 'In Progress';
        }

        constructedRecords.push({
          id: `camp_assn_${key}`,
          learnerId: a.employee_id,
          requiredTraining: a.campaign_id,
          dueDate: campaign.endDate,
          completionDate: isCompleted ? campaign.endDate : null,
          complianceStatus,
          requirementType: campaign.campaignType || 'Training',
          requirement: campaign.campaignName || 'Unknown Campaign',
          source: 'assignment'
        });
      });

      // Include phishing simulations as compliance records
      recipients.forEach(r => {
        const sim = simulations.find(s => String(s.id) === String(r.campaign_id));
        if (!sim) return;

        let complianceStatus = 'Compliant';
        if (r.status === 'Clicked') complianceStatus = 'Overdue';
        else if (r.status === 'Reported') complianceStatus = 'Compliant';
        else if (new Date(sim.date) < today && r.status !== 'Clicked') complianceStatus = 'Compliant';
        else complianceStatus = 'Not Started';

        constructedRecords.push({
          id: `phish_${r.id}`,
          learnerId: r.employee_id,
          requiredTraining: r.campaign_id,
          dueDate: sim.date,
          completionDate: sim.date, // Set to sim date to prevent showing in "Upcoming Deadlines" falsely
          complianceStatus,
          requirementType: 'Phishing Simulation',
          requirement: sim.name || 'Phishing Test',
          source: 'phishing'
        });
      });

      setRecords(constructedRecords);
      setLearners(employees);
      setCampaigns(campaignsData);
      setLoading(false);
    });
  }, [year]); // Refetch when the year alters

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 max-w-[1400px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <PageHeader eyebrow="Governance" title="Compliance" description="Track required training and policy acknowledgements across your workforce." />
        <div className="flex items-center gap-3">
          <select 
            value={year} 
            onChange={e => setYear(Number(e.target.value))}
            className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 font-medium cursor-pointer shadow-sm"
          >
            <option value={new Date().getFullYear()}>Current Year ({new Date().getFullYear()})</option>
            <option value={new Date().getFullYear() - 1}>Previous Year ({new Date().getFullYear() - 1})</option>
          </select>

          <div className="flex gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-sm flex-shrink-0">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-[#0f172a] text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
                <SafeIcon icon={t.icon} className="text-sm" />{t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <SafeIcon icon={FiLoader} className="text-4xl mb-3 animate-spin" />
          <p className="font-semibold text-lg">Loading compliance data...</p>
        </div>
      ) : (
        <>
          <ComplianceMetrics records={records} learners={learners} />

          <AnimatePresence mode="wait">
            {tab === 'overview' && (
              <motion.div key="overview" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="space-y-6">
                <ComplianceTrendChart records={records} />
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2"><DepartmentComplianceChart records={records} learners={learners} /></div>
                  <ComplianceStatusBreakdown records={records} />
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <UpcomingDeadlines records={records} learners={learners} campaigns={campaigns} />
                  <RequirementTypeSummary records={records} />
                </div>
              </motion.div>
            )}
            {tab === 'records' && (
              <motion.div key="records" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                <ComplianceTable records={records} learners={learners} campaigns={campaigns} />
              </motion.div>
            )}
          </AnimatePresence>
        </>
      )}
    </motion.div>
  );
};

export default Compliance;